import { Injectable } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { Observable, of, timer } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { UserService } from '../services/core/user.service';

/**
 * Preloads lazy feature modules, but never while the user is sitting on the login page.
 *
 * `PreloadAllModules` starts downloading all 35+ feature chunks the moment the first route
 * renders. On the login route that means tens of megabytes of Syncfusion (PDF viewer, document
 * editor, spreadsheet) competing with the login form for network and main-thread time, while
 * being useless to a user who is not signed in yet.
 *
 * Signed out -> preload nothing. Signed in -> preload, but only after a short idle delay so the
 * chunk downloads never contend with the first screen's own rendering and API calls.
 */
@Injectable({ providedIn: 'root' })
export class AuthenticatedPreloadStrategy implements PreloadingStrategy {
  /** Long enough for the landing route to finish painting and firing its initial requests. */
  private static readonly PRELOAD_DELAY_MS = 3000;

  constructor(private currentUser: UserService) {}

  preload(route: Route, load: () => Observable<any>): Observable<any> {
    if (!this.currentUser.isLoggedIn()) {
      return of(null);
    }

    // Routes can opt out individually with `data: { preload: false }`.
    if (route.data && route.data['preload'] === false) {
      return of(null);
    }

    return timer(AuthenticatedPreloadStrategy.PRELOAD_DELAY_MS).pipe(mergeMap(() => load()));
  }
}
